import React, { ReactElement, useState, useEffect, createRef } from "react";
import { Translate } from "react-redux-i18n";
import { useAppSelector, useAppDispatch } from "redux/hooks";
import {
  Typography,
  Box,
  Button,
  Tabs
} from "@mui/material";
import styles from './styles.module.scss'
import { calculateAllCombinationsForCheckedGroups, calculateBonusArrayPerGroupPlayedCoupon, calculateMultiplicationOfSystemOddCombPlayedCoupon } from "redux/coupon/coupon.utils";
import { SystemCombination, saveCombinationFilter, } from "redux/coupon/couponSlice";
import FontAwesomeIcon from "components/shared/FontAwesomeIcon";
import { handleScroll, convertOddValue } from "services/utils";
import { saveBettingWalletId } from "redux/wallet/walletSlice";

export default function SystemCombinations(): ReactElement {
  const dispatch = useAppDispatch();
  const playedCoupon = useAppSelector(state => state.coupon.playedCoupon);
  const bonus = useAppSelector(state => state.coupon.bettingConfig?.bonus);
  const rankBonus = useAppSelector(state => state.coupon.bettingConfig?.setting?.rank_bonus);
  const currency = useAppSelector(state => state.auth.authWallet?.currency.symbol);
  const combinationFilter = useAppSelector(state => state.coupon.combinationFilter);
  const limit = useAppSelector(state => state.coupon.bettingConfig?.setting["win_system"]);
  const walletId = useAppSelector(state => state.auth.authWallet?.id)
  const playedCouponWalletId = useAppSelector(state => state.wallet.playedCouponWalletId)
  const divider = useAppSelector(state => state.auth.authWallet?.currency.divider)
  const decimal = useAppSelector(state => state.auth.authWallet?.currency.decimal)

  const [selectedGroup, setSelectedGroup] = useState<any>();
  const [combinations, setCombinations] = useState<SystemCombination[][]>()
  const [itemsToLoad, setItemsToLoad] = useState<number>(20);
  const [value, setValue] = useState<number>(0)

  const ref: any = createRef();

  const handleTabChange = (e: React.SyntheticEvent, newValue: number) => {
    setValue(newValue)
  }

  useEffect(() => {
    playedCoupon &&
      setSelectedGroup(playedCoupon.systems[0])
  }, [playedCoupon])
  useEffect(() => {
    dispatch(saveCombinationFilter("all"))
  }, [])

  useEffect(() => {
    const groupCombinations = playedCoupon && selectedGroup && calculateAllCombinationsForCheckedGroups(playedCoupon.odds, selectedGroup.group, playedCoupon.fixed)
    if (ref && ref.current) {
      ref.current.scrollTop = 0;
    }
    switch (combinationFilter) {
      case "all":
        setCombinations(groupCombinations)
        break;
      case "winner":
        setCombinations(groupCombinations?.filter((combo: SystemCombination[]) => combo.every((odd: SystemCombination) => (odd.odd.winner === true || odd.odd.void === true))))
        break;
      case "running":
        setCombinations(groupCombinations?.filter((combo: SystemCombination[]) =>
          !combo.find((odd: SystemCombination) => (odd.odd.running === false && odd.odd.winner === false && odd.odd.void === false)) &&
          !combo.every((odd: SystemCombination) => (odd.odd.winner === true || odd.odd.void === true))
        ))
        break;
      case "loser":
        setCombinations(groupCombinations?.filter((combo: SystemCombination[]) => combo.find((odd: SystemCombination) => odd.odd.winner === false && odd.odd.running === false && odd.odd.void === false)))
        break;
      default:
        setCombinations(groupCombinations)
        break;
    }
    setItemsToLoad(20)
  }, [playedCoupon, selectedGroup, combinationFilter])

  useEffect(() => {
    dispatch(saveCombinationFilter("all"))
    if (walletId && playedCouponWalletId && walletId !== playedCouponWalletId) {
      dispatch(saveBettingWalletId(playedCouponWalletId))
    } else if (walletId && playedCouponWalletId && walletId === playedCouponWalletId) {
      dispatch(saveBettingWalletId(walletId))
    }
    return () => {
      walletId && dispatch(saveBettingWalletId(walletId))
    }
  }, [])

  return (
    <Box className={styles.container}>
      {
        selectedGroup &&
        combinations &&
        <Box ref={ref} onScroll={(e) => handleScroll(e, setItemsToLoad, itemsToLoad, 20)} className={styles.combinationsGrid}>
          <Typography className={styles.groupTitle}>
            Gruppo {selectedGroup.group}
          </Typography>
          {combinations?.length <= 0 &&
            <Typography className={styles.noCombinationsMessage}><Translate value="couponDetail.noCombinationsFound" /></Typography>
          }
          <Box className={styles.gridContainer} >
            {
              combinations.slice(0, itemsToLoad).map((comb: SystemCombination[], index: number) => {
                const win = calculateMultiplicationOfSystemOddCombPlayedCoupon([comb]) * selectedGroup.bet_amount
                const bon = calculateBonusArrayPerGroupPlayedCoupon(
                  [comb],
                  selectedGroup.bet_amount,
                  bonus,
                  rankBonus
                );
                return <Box key={index} className={styles.combinationContainer}>
                  <Box className={styles.combinationHeader}>
                    <Typography className={styles.headerLabel} sx={{ width: "40%" }}><Translate value="couponDetail.combinationsEvent" /></Typography>
                    <Typography className={styles.headerLabel} sx={{ width: "30%" }}><Translate value="couponDetail.combinationsMarket" /></Typography>
                    <Typography className={styles.headerLabel} sx={{ width: "15%" }}><Translate value="couponDetail.combinationsOdd" /></Typography>
                    <Typography className={styles.headerLabel} sx={{ width: "15%" }}><Translate value="couponDetail.combinationsValue" /></Typography>
                  </Box>
                  {
                    comb.map((el: SystemCombination) => {
                      return <Box key={el.unique} className={styles.combinationContent}>
                        <Typography className={styles.contentLabel} sx={{ width: "40%" }}>{el.event.label}</Typography>
                        <Typography className={styles.contentLabel} sx={{ width: "30%" }}>{el.market.label}</Typography>
                        <Typography className={styles.contentLabel} sx={{ width: "15%" }}>{el.odd.label}</Typography>
                        <Box className={styles.lastCell}>
                          <Typography className={styles.contentLabel} sx={{ width: "15%" }}>{el.odd.void ? 1 : convertOddValue(el.value)}</Typography>
                          <Box className={
                            //when its running = yellow , void = white , winner green , else red
                            `
                            ${styles.state}
                            ${el.odd.running
                              ? styles.stateYellow
                              : el.odd.winner
                                ? styles.stateGreen
                                : el.odd.void
                                  ? styles.stateWhite
                                  : styles.stateRed}`

                          }
                          >
                            <FontAwesomeIcon type="fas" name="circle" />
                          </Box>
                        </Box>

                      </Box>
                    })
                  }
                  <Box className={styles.calcContainer}>
                    <Box className={styles.calcContainerFirstItem}>
                      <Typography className={styles.totalLabel} color="white">
                        <Translate value="couponDetail.combinationsAmountCol" />
                      </Typography>
                      <Typography className={styles.totalLabel} >
                        {currency} {divider && (selectedGroup.bet_amount / divider).toFixed(4)}
                      </Typography>
                    </Box>
                    <Box className={styles.calcContainerItem}>
                      <Typography className={styles.totalLabel} ><Translate value="couponDetail.combinationsWin" /></Typography>
                      <Typography className={styles.totalLabel} >{currency} {divider && (limit && (win > limit) ? (limit / divider).toFixed(decimal) : (win / divider).toFixed(decimal))}</Typography>
                    </Box>
                    <Box className={styles.calcContainerItem}>
                      <Typography className={styles.totalLabel} ><Translate value="couponDetail.combinationsBonus" /></Typography>
                      <Typography className={styles.totalLabel} >{currency} {divider && bon.length > 0 ? (limit && bon[0] > limit ? (limit / divider).toFixed(decimal) : (bon[0] / divider).toFixed(decimal)) : 0.00} </Typography>
                    </Box>

                    <Box className={styles.calcContainerLastItem}>
                      <Typography className={styles.totalLabel} ><Translate value="couponDetail.combinationsTotalWin" /></Typography>
                      <Typography className={styles.totalLabel} >{currency} {
                        divider &&
                        (bon.length > 0 ? (limit && (win + bon[0]) > limit ? limit / divider : (win + bon[0]) / divider).toFixed(decimal) :
                          limit && win > limit ? (limit / divider).toFixed(decimal) :
                            (win / divider).toFixed(decimal))
                      }</Typography>
                    </Box>
                  </Box>
                </Box>
              })
            }
          </Box>

        </Box>
      }

      <Box className={styles.groupPaginatorContainer}>
        <Tabs
          value={value}
          onChange={handleTabChange}
          scrollButtons={true}
          orientation="horizontal"
          variant="scrollable"
          allowScrollButtonsMobile
          TabScrollButtonProps={{ className: `${styles.tabsScrollButtons}` }}
          className={styles.tabs}
        >
          {
            playedCoupon &&
            playedCoupon.systems.map(item =>
              <Button
                key={item.group}
                variant="contained"
                onClick={() => setSelectedGroup(item)}
                className={`${styles.groupBtn} ${selectedGroup?.group === item.group ? styles.selectedGroupBtn : ""}`}
              >
                <Typography variant="body1" color="white">
                  {item.group}
                </Typography>
              </Button>
            )
          }
        </Tabs>
      </Box>
    </Box >
  )
}
